import {
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from "@mui/material";
import { DataTableHead } from "./data-table-head";
import { DataTableRow } from "./data-table-row";
import classNames from "classnames";
import { DataTableProps } from "./types";
import { useDataTableValues } from "./utils";
import { DataTableSelectedControls } from "./data-table-selected-controls";
import { styleConstants } from "../../utils/colors";

export const DataTable = ({
  data,
  labels,
  getKey,
  className,
  formatCell,
  sx,
  disabledRows,
  bottomWidget,
  onEdit,
  onDelete,
  selectedRows,
  onSelect,
  onDeselect,
  storageKey = "data-table",
  perPageOptions,
  perPage,
  page,
  total,
  onPageChange,
  onPerPageChange,
  hiddenColumns = [],
}: DataTableProps) => {
  const addSelectedRow = (id: string) => onSelect && onSelect(id);
  const removeSelectedRow = (id: string) => onDeselect && onDeselect(id);
  const {
    allRowIds,
    selectedRowsSet,
    disabledRowsSet,
    isAllSelected,
    headers,
  } = useDataTableValues({
    getKey,
    data,
    disabledRows,
    selectedRows,
  });

  const isSelectedControlsVisible =
    selectedRowsSet.size > 0 && (onEdit || onDelete);

  const shouldShowFooterControls = isSelectedControlsVisible || bottomWidget;
  const isSelectable = !!onSelect && !!onDeselect;

  const isPaginationVisible =
    perPageOptions &&
    perPageOptions.length > 0 &&
    perPage !== undefined &&
    page !== undefined;

  return (
    <>
      <Paper
        sx={{
          width: "100%",
          overflow: "auto",
          ...sx,
          scrollbarColor: (theme) =>
            theme.palette.mode === "dark" ? "#555 #111" : "#aaa #eee",
        }}
        elevation={2}
        className="data-table-container"
      >
        <TableContainer
          sx={{
            // xs subtracts the mobile sidebar bar (styleConstants.mobileSidebarHeight)
            // plus the table's own fixed chrome, so this stays correct if that bar's height changes.
            maxHeight: {
              xs: `calc(100vh - ${styleConstants.mobileSidebarHeight} - ${shouldShowFooterControls ? 174 : 132}px)`,
              md: "calc(100vh - 168px)",
            },
          }}
        >
          <Table stickyHeader className={classNames("data-table", className)}>
            <DataTableHead
              storageKey={storageKey}
              isAllSelected={isAllSelected}
              selectAll={
                isSelectable && selectedRows
                  ? () => onSelect(allRowIds)
                  : undefined
              }
              headers={headers}
              labels={labels}
              selectedCount={selectedRowsSet.size}
              selectAllDisabled={data.length === 0}
              hiddenColumns={hiddenColumns}
            />

            <TableBody className="data-table-body">
              {data.map((row, i) => {
                const rowId = getKey(row) as string;
                return (
                  <DataTableRow
                    formatCell={formatCell}
                    disabled={disabledRowsSet.has(rowId)}
                    selected={selectedRowsSet.has(rowId)}
                    onSelect={
                      isSelectable
                        ? () => {
                            const id = rowId;
                            if (selectedRowsSet.has(id)) {
                              removeSelectedRow(id);
                            } else {
                              addSelectedRow(id);
                            }
                          }
                        : undefined
                    }
                    key={rowId}
                    data={row}
                    hiddenColumns={hiddenColumns}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        {isPaginationVisible && (
          <TablePagination
            rowsPerPageOptions={perPageOptions || []}
            component="div"
            count={total ?? data.length}
            rowsPerPage={perPage}
            page={page - 1}
            onPageChange={(e, page) => onPageChange && onPageChange(page + 1)}
            onRowsPerPageChange={(e) =>
              onPerPageChange && onPerPageChange(parseInt(e.target.value, 10))
            }
          />
        )}
      </Paper>

      {shouldShowFooterControls && (
        <Stack
          direction="row"
          spacing={1}
          alignContent={"flex-end"}
          alignItems={"flex-end"}
          justifyContent="flex-end"
          sx={{
            p: 1,
            pb: {
              xs: 0,
              md: 1,
            },
          }}
        >
          {isSelectedControlsVisible && (
            <DataTableSelectedControls onEdit={onEdit} onDelete={onDelete} />
          )}
          {bottomWidget && bottomWidget}
        </Stack>
      )}
    </>
  );
};
